import { formatMoney } from '../../lib/money';
import { STREAMS, type SalesSummary, type Stream } from './api';

/** The part of an ECharts axis-tooltip param the formatter reads. */
export interface TooltipParam {
  seriesId?: string;
  seriesName?: string;
  dataIndex: number;
  /** ECharts' coloured dot for the series, as HTML it builds itself. */
  marker?: unknown;
}

/** The series id the chart gives last year's line. */
export const LAST_YEAR_SERIES = 'LAST_YEAR';

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const isStream = (id: string | undefined): id is Stream => STREAMS.some((s) => s === id);

/**
 * The tooltip for the revenue chart.
 *
 * The bars are plotted from numbers, but a person reads the figure, so each
 * value is looked up again as the API's decimal string and formatted from
 * that. A float rounded to cents is close, not equal, and money on this
 * screen is meant to match AutoCount to the sen.
 */
export function salesTooltipFormatter(
  summary: SalesSummary,
  monthLabel: (month: number) => string,
): (params: TooltipParam | TooltipParam[]) => string {
  return (params) => {
    const list = Array.isArray(params) ? params : [params];
    const month = summary.months[list[0]?.dataIndex ?? -1];

    const rows = list.map((p) => {
      const m = summary.months[p.dataIndex];
      let value: string | undefined;
      if (m !== undefined) {
        if (p.seriesId === LAST_YEAR_SERIES) value = m.lastYearRevenue;
        else if (isStream(p.seriesId)) value = m.byStream[p.seriesId].revenue;
      }
      const marker = typeof p.marker === 'string' ? p.marker : '';
      const amount = value === undefined ? '—' : formatMoney(value);
      return (
        `<div style="display:flex;justify-content:space-between;gap:16px">` +
        `<span>${marker}${escapeHtml(p.seriesName ?? '')}</span>` +
        `<strong style="font-variant-numeric:tabular-nums">${escapeHtml(amount)}</strong></div>`
      );
    });

    const title = month === undefined ? '' : `<div>${escapeHtml(monthLabel(month.month))}</div>`;
    return title + rows.join('');
  };
}

/**
 * The tooltip for a chart of quantities by month. `valueOf` looks the figure up
 * again as the API's decimal string, so what is read is formatted from that
 * and never from the plotted float.
 */
export function unitsTooltipFormatter(
  valueOf: (seriesId: string | undefined, dataIndex: number) => string | undefined,
  titleOf: (dataIndex: number) => string,
): (params: TooltipParam | TooltipParam[]) => string {
  return (params) => {
    const list = Array.isArray(params) ? params : [params];
    const rows = list.map((p) => {
      const marker = typeof p.marker === 'string' ? p.marker : '';
      return (
        `<div style="display:flex;justify-content:space-between;gap:16px">` +
        `<span>${marker}${escapeHtml(p.seriesName ?? '')}</span>` +
        `<strong style="font-variant-numeric:tabular-nums">${escapeHtml(valueOf(p.seriesId, p.dataIndex) ?? '—')}</strong></div>`
      );
    });
    return `<div>${escapeHtml(titleOf(list[0]?.dataIndex ?? 0))}</div>${rows.join('')}`;
  };
}
